/**
 * ContinuousDocument Component (Refactored)
 * 
 * Main editor component for continuous document editing with TipTap.
 * This is a refactored version split into modular components.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef } from 'react';
import { ChevronUp, ChevronDown, MessageSquare } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import FontFamily from '@tiptap/extension-font-family';
import { NodeSelection } from 'prosemirror-state';
import { DOMSerializer } from 'prosemirror-model';
import { toast } from 'sonner';

// Local imports
import FontSize from '../../extensions/FontSizeExtension';
import { safeNodeAt } from '../../lib/safeNodeAt';
import { CommentMark, CommentMarker } from '../../extensions/CommentExtension';
import ReferencesExtension from '../../extensions/ReferencesExtension'; // T-246: chips de referencias en el editor
import ResizableImage, { ImageUploadDialog, ImageGenerateDialog, ImageGallery } from '../ImageNode';
import InsertTableDialog from '../InsertTableDialog';
import ConfirmDialog from '../ConfirmDialog';

// TipTap table extensions
import { Table, TableRow, TableHeader, TableCell, TableView } from '@tiptap/extension-table';

// Module imports
import EditorToolbar from './EditorToolbar';
import TableFloatingToolbar from './TableFloatingToolbar';
import { normalizeDocumentContent, isInternalProseMirrorPaste, selectionSignature } from './utils';
import { parseTableData, detectTableFormat, generateInitialContent } from '../../lib/table-parser';
import { migrateSmartTables } from '../../lib/tableMigration';
import { serializeCellSelection } from '../../lib/tableSerializer';
import { sanitizeTableHtml } from '../../lib/officeHtmlSanitizer';
import { useTextInsertion } from './hooks/useTextInsertion';

// Styles
import '../../styles/editor.css';

// The resizable TableView builds the <table> element by hand and copies ONLY
// node.attrs.style — so a whole-table comment's `commentId` attr never reaches
// the DOM, breaking hover-highlight. Mirror it onto the element here (and keep it
// in sync on updates). No visible ::after badge — the toolbar button is the cue.
class CommentableTableView extends TableView {
  constructor(node, cellMinWidth) {
    super(node, cellMinWidth);
    this.syncComment(node);
  }

  update(node) {
    const handled = super.update(node);
    if (handled) this.syncComment(node);
    return handled;
  }

  syncComment(node) {
    const id = node.attrs.commentId;
    if (id && !node.attrs.commentResolved) {
      this.table.setAttribute('data-comment-id', id);
    } else {
      this.table.removeAttribute('data-comment-id');
    }
  }
}

/**
 * ContinuousDocument - Main editor component
 * 
 * Args:
 *   content (object|string): Initial content for the editor
 *   onUpdate (function): Callback when content changes
 *   onTextSelection (function): Callback when text is selected
 *   onTableOptionsClick (function): Callback for table options clicks
 *   ref (ref): Forwarded ref for parent access
 * 
 * Returns:
 *   JSX.Element: The continuous document editor
 */
const ContinuousDocument = forwardRef(({
  content,
  onUpdate,
  onTextSelection,
  onTableOptionsClick,
  landscape = false,
  chatOpen,
  onToggleChat,
  zoom = 1,
}, ref) => {
  // Refs
  const updateTimeoutRef = useRef(null);
  const lastSelectionRef = useRef('');
  const lastSelectionRangeRef = useRef({ from: 0, to: 0 });
  const buttonPositionRef = useRef(null);
  // #4: the last selection payload emitted, so a layout resize (chat/comments/
  // properties toggling) can re-emit it at a freshly-computed position instead
  // of leaving the floating buttons stranded where the paper used to be.
  const lastPayloadRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const selectDropdownSelectionRef = useRef(null);
  const cursorPositionRef = useRef(null);
  const editorContainerRef = useRef(null);
  const isSelectingRef = useRef(false);
  const scrollRafIdRef = useRef(null);

  // State
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [toolbarCollapsed, setToolbarCollapsed] = useState(() => {
    try { return localStorage.getItem('editor.toolbarCollapsed') === '1'; } catch { return false; }
  });
  const toggleToolbarCollapsed = () => {
    setToolbarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('editor.toolbarCollapsed', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };
  const [imageGenerateDialogOpen, setImageGenerateDialogOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [isInsertTableDialogOpen, setInsertTableDialogOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodePos, setSelectedNodePos] = useState(null);
  const [pastedText, setPastedText] = useState(null);
  const [pastedPlainText, setPastedPlainText] = useState(null);
  const [detectedFormat, setDetectedFormat] = useState(null);
  const [isConfirmingPaste, setConfirmingPaste] = useState(false);
  const [pastePosition, setPastePosition] = useState(null);

  // Reason: migrate legacy smartTable nodes to native tables BEFORE content enters
  // the editor (the smartTable extension is unregistered — an unmigrated node would crash).
  const normalizedContentValue = useMemo(
    () => normalizeDocumentContent(migrateSmartTables(content)),
    [content]
  );

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Reason: Disable default paste handling for clipboard text to implement custom table detection
        // We'll handle paste manually in editorProps
      }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      FontFamily.configure({
        types: ['textStyle']
      }),
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph', 'resizableImage', 'tableCell', 'tableHeader'],
      }),
      ResizableImage,
      // Reason (CONTRATO v3, cambio 4): native table nodes are the ONE canonical
      // in-editor table representation. The legacy `smartTable` extension is gone;
      // legacy nodes are migrated to native tables on load (see migrateSmartTables).
      // Whole-table comment anchors on the table node (commentId attr → the
      // CommentableTableView mirrors it to data-comment-id on <table> for hover),
      // surfaced by the amber comment button in TableFloatingToolbar. Cell text is
      // left un-highlighted; partial cell comments use a mark instead.
      Table.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            commentId: {
              default: null,
              parseHTML: (el) => el.getAttribute('data-comment-id'),
              renderHTML: (attrs) => (attrs.commentId ? { 'data-comment-id': attrs.commentId } : {}),
            },
            commentResolved: {
              default: false,
              parseHTML: (el) => el.getAttribute('data-comment-resolved') === 'true',
              renderHTML: (attrs) => (attrs.commentResolved ? { 'data-comment-resolved': 'true' } : {}),
            },
          };
        },
      }).configure({
        resizable: true,
        View: CommentableTableView,
        HTMLAttributes: {
          class: 'table-align-left',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      // T-057: notas/comentarios — mark de rango sobre texto seleccionado + nodo
      // marcador inline para notas en posición de cursor. Ambos anclan por commentId.
      CommentMark,
      CommentMarker,
      // T-246: extension de referencias — chips en bloques citados del editor
      ReferencesExtension,
    ],
    content: normalizedContentValue || '',
    editorProps: {
      // T-042.img: make click-to-select the image atom deterministic. Because
      // the image NodeView is a drag source, the browser mousedown starts a drag
      // and the atom never gets a NodeSelection on a plain click (no border, no
      // panel). Force the NodeSelection here — the canonical ProseMirror hook.
      // T-042.img: make click-to-select the image atom deterministic. The
      // NodeView was a drag source (draggable + data-drag-handle) which made the
      // browser swallow the selecting click; that's now disabled, and these two
      // hooks guarantee a NodeSelection whether ProseMirror reports the click on
      // the node (handleClickOn) or resolves it to the wrapper DOM (handleClick).
      handleClickOn: (view, pos, node, nodePos) => {
        if (node && node.type && node.type.name === 'resizableImage') {
          view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos)));
          return true;
        }
        return false;
      },
      handleClick: (view, pos, event) => {
        const el = event.target && event.target.closest && event.target.closest('.resizable-image-wrapper');
        if (el) {
          try {
            const domPos = view.posAtDOM(el, 0);
            let imgPos = null;
            for (const cand of [domPos - 1, domPos, pos, pos - 1]) {
              const n = cand >= 0 ? view.state.doc.nodeAt(cand) : null;
              if (n && n.type.name === 'resizableImage') { imgPos = cand; break; }
            }
            if (imgPos !== null) {
              view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, imgPos)));
              return true;
            }
          } catch (e) { /* fall through to default handling */ }
        }
        return false;
      },
      // #14: ProseMirror's default Backspace/Delete leaves a table node intact
      // when a non-empty selection only PARTIALLY covers it (starts before and
      // ends inside a cell) — the open-ended replace preserves table structure,
      // so "the table doesn't get deleted". Detect a selection that STRADDLES a
      // table boundary and delete that table's full range so the whole selection
      // (text + table) is removed. Selections wholly inside one table (cell text,
      // incl. CellSelection) and those already covering a table completely fall
      // through to the default handler.
      handleKeyDown: (view, event) => {
        if (event.key !== 'Backspace' && event.key !== 'Delete') return false;
        const { selection, doc } = view.state;
        if (selection.empty) return false;
        const { from, to } = selection;
        let straddles = false;
        let rangeFrom = from;
        let rangeTo = to;
        doc.nodesBetween(from, to, (node, pos) => {
          if (node.type.name === 'table') {
            const nodeFrom = pos;
            const nodeTo = pos + node.nodeSize;
            const fullyInside = from >= nodeFrom && to <= nodeTo;
            if (!fullyInside) {
              straddles = true;
              rangeFrom = Math.min(rangeFrom, nodeFrom);
              rangeTo = Math.max(rangeTo, nodeTo);
            }
          }
        });
        if (!straddles) return false;
        view.dispatch(view.state.tr.deleteRange(rangeFrom, rangeTo));
        return true;
      },
      handlePaste: (view, event, slice) => {
        const html = event.clipboardData?.getData('text/html');
        const text = event.clipboardData?.getData('text/plain') || '';

        // Reason (T-017 / Complaint B): a copy made INSIDE the editor (e.g. a
        // table cell-range) lands on the clipboard as ProseMirror's own HTML,
        // which carries a data-pm-slice marker AND a <table>. Let ProseMirror
        // paste it natively instead of hijacking it with the table dialog.
        if (isInternalProseMirrorPaste(html)) {
          return false;
        }

        // Reason: Intercept table paste BEFORE TipTap processes it
        if (html && /<table/i.test(html)) {
          console.log('Table detected in paste (handlePaste), intercepting...');

          // Reason: Save cursor position BEFORE showing dialog
          const { from } = view.state.selection;
          setPastePosition(from);

          // Show confirmation dialog BEFORE pasting anything
          setPastedText(html);
          setPastedPlainText(text);
          setDetectedFormat('html');
          setConfirmingPaste(true);

          // Return true to prevent TipTap's default paste handling
          return true;
        }

        // Check if plain text contains table data (TSV, CSV, Markdown)
        if (text) {
          const format = detectTableFormat(text);
          if (format && format !== 'html') {
            console.log('Table format detected in plain text (handlePaste):', format);

            // Reason: Save cursor position BEFORE showing dialog
            const { from } = view.state.selection;
            setPastePosition(from);

            // Show confirmation dialog BEFORE pasting anything
            setPastedText(text);
            setPastedPlainText(text);
            setDetectedFormat(format);
            setConfirmingPaste(true);

            // Return true to prevent TipTap's default paste handling
            return true;
          }
        }

        // Return false to allow TipTap's default paste handling for non-table content
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      // Debounce updates to avoid rapid fire changes
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      updateTimeoutRef.current = setTimeout(() => {
        // Reason: Store content as JSON instead of HTML for better structure preservation
        const json = editor.getJSON();
        onUpdate(json);
      }, 500);
    },
    onSelectionUpdate: ({ editor }) => {
      const selection = editor.state.selection;
      const { from, to } = selection;
      const isNodeSelection = selection instanceof NodeSelection;
      const nodeType = isNodeSelection ? selection.node?.type?.name : null;

      // Preserve last range while a dropdown is open to avoid losing selection
      const hasRange = savedSelectionRef.current && savedSelectionRef.current.from !== savedSelectionRef.current.to;
      if (!(from === to && hasRange)) {
        // Save selection for toolbar commands (even cursor position when no dropdown open)
        savedSelectionRef.current = { from, to, isNodeSelection, nodeType };
      }

      // Check if an image is selected (support both 'image' and 'resizableImage')
      const node = safeNodeAt(editor.state.doc, from);
      if (node && (node.type.name === 'image' || node.type.name === 'resizableImage')) {
        setSelectedNode(node);
        setSelectedNodePos(from);
      } else {
        setSelectedNode(null);
        setSelectedNodePos(null);
      }

      // Extract both text and images from selection
      const selectedText = editor.state.doc.textBetween(from, to, ' ');
      const trimmedText = selectedText ? selectedText.trim() : '';

      // Extract images from selection
      const images = [];
      editor.state.doc.nodesBetween(from, to, (node) => {
        if (node.type.name === 'image' || node.type.name === 'resizableImage') {
          images.push({
            src: node.attrs.src,
            alt: node.attrs.alt || '',
            title: node.attrs.title || '',
            width: node.attrs.width || null,
            height: node.attrs.height || null
          });
        }
      });

      // Reason: Serialize selection for complete AI context (including tables).
      // For a rectangular table selection (CellSelection) we hand the AI GFM —
      // or HTML when the rectangle has merged cells, per CONTRATO v3 (the GFM
      // channel falls back to HTML for merges). For everything else we fall back
      // to DOMSerializer HTML, which handles selections spanning table ↔ doc
      // boundaries.
      let selectedHTML = '';
      let hasTables = false;
      let tableGfm = null;

      if (from !== to) {
        const cellResult = serializeCellSelection(editor.state);

        if (cellResult) {
          // Rectangular table selection: GFM is the primary payload; only route
          // through the HTML channel (has_tables=true) when merges force it.
          tableGfm = cellResult.gfm;
          if (cellResult.hasMerges) {
            selectedHTML = cellResult.html;
            hasTables = true;
          }
        } else {
          try {
            const slice = editor.state.selection.content();

            // Reason: Use DOMSerializer.fromSchema for reliable cross-boundary serialization
            // This correctly handles selections spanning table ↔ document boundaries
            const serializer = DOMSerializer.fromSchema(editor.schema);

            if (slice.content) {
              const fragment = serializer.serializeFragment(slice.content);
              const tempDiv = document.createElement('div');
              tempDiv.appendChild(fragment);
              selectedHTML = tempDiv.innerHTML;

              // Check if selection contains tables
              editor.state.doc.nodesBetween(from, to, (node) => {
                if (node.type.name === 'table') {
                  hasTables = true;
                }
              });
            }
          } catch (error) {
            // Reason (T-013): never silently drop table context. Degrade
            // gracefully (plain text still flows below) but surface the failure.
            console.warn('Failed to serialize selection to HTML:', error);
            selectedHTML = '';
            hasTables = false;
          }
        }
      }

      // Create a signature for the selection (text + image count + tables)
      const signature = selectionSignature({ trimmedText, images, hasTables, tableGfm });

      // Only fire event if selection actually changed
      if (signature !== lastSelectionRef.current) {
        lastSelectionRef.current = signature;

        if (onTextSelection) {
          if (trimmedText.length > 0 || images.length > 0 || hasTables || tableGfm) {
            // Reason: Calculate position RELATIVE to editor container, not viewport
            // Position button on RIGHT side of white page, in the gray area
            const coords = editor.view.coordsAtPos(from);
            const containerRect = editorContainerRef.current?.getBoundingClientRect();

            if (containerRect) {
              // Get the actual page ("paper") element — theme-aware class (T-020)
              const pageElement = editorContainerRef.current?.querySelector('.editor-paper');
              const pageRect = pageElement?.getBoundingClientRect();

              if (pageRect) {
                // Convert viewport coords to container-relative coords
                const topRelativeToContainer = coords.top - containerRect.top + editorContainerRef.current.scrollTop;

                // Calculate right position: right edge of page + small margin (10px)
                // pageRect.right is viewport coords, convert to container-relative
                const pageRightRelativeToContainer = pageRect.right - containerRect.left;
                // T-118: en tablet el papel llena el canvas (maxWidth:100%), asi que el borde
                // derecho + 10 caeria fuera de la vista. Clamp para que el boton siga alcanzable.
                const leftPosition = Math.min(pageRightRelativeToContainer + 10, editorContainerRef.current.clientWidth - 48);

                buttonPositionRef.current = { top: topRelativeToContainer, left: leftPosition };

                const payload = {
                  // Reason (T-013): for a rectangular table selection the GFM
                  // is the AI-facing payload (routed as plaintext when no
                  // merges); otherwise the raw selected text.
                  text: tableGfm || trimmedText,
                  images,
                  html: selectedHTML, // Full HTML representation including tables (merges)
                  hasTables
                };
                lastPayloadRef.current = payload; // #4
                onTextSelection(
                  payload,
                  { top: buttonPositionRef.current.top, left: buttonPositionRef.current.left, visible: true },
                  'selection'
                );
              }
            }
          } else {
            // No selection - clear
            buttonPositionRef.current = null;
            lastPayloadRef.current = null; // #4
            // #13: NO resetear lastSelectionRef aqui. L433 ya la fijo a la firma
            // "sin seleccion" (`|0||false|0`); resetearla a '' hacia que la guardia
            // de L432 nunca cortara -> onTextSelection disparaba en CADA tecla ->
            // re-render de EditorPage por tecla -> multiplicado por cada NodeView
            // de imagen (sin memo). La firma sola ya distingue seleccion nueva.
            onTextSelection(
              { text: '', images: [], html: '', hasTables: false },
              { top: 0, left: 0, visible: false },
              'cursor'
            );
          }
        }
      }
    },
  });

  // El autosave tiene 500ms de debounce. Sin esto, teclear y cerrar la pestaña
  // (o navegar al dashboard) dentro de esa ventana PERDÍA la última edición en
  // silencio: sin aviso, sin error, y el usuario creyendo que guardó.
  //
  // Por qué el flush de desmontaje escribe en la hoja CORRECTA: este componente
  // se monta con `key={doc-${activeSheetId}}`, así que cambiar de hoja DESTRUYE
  // esta instancia y crea otra. La instancia que se desmonta nunca re-renderiza
  // con el id nuevo, así que su `onUpdate` sigue apuntando a SU hoja — la vieja,
  // que es justo el destino correcto del contenido pendiente.
  const flushRef = useRef(null);
  flushRef.current = () => {
    if (!updateTimeoutRef.current) return;
    clearTimeout(updateTimeoutRef.current);
    updateTimeoutRef.current = null;
    if (editor && onUpdate) onUpdate(editor.getJSON());
  };

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!updateTimeoutRef.current) return; // nada pendiente: no molestar
      flushRef.current?.();
      // El PUT disparado aquí puede no llegar a completar: el navegador no espera
      // peticiones async en beforeunload. Lo que SÍ es fiable es el prompt — si el
      // usuario cancela y se queda, el debounce normal termina de guardar.
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Desmontaje (cambio de hoja, navegación interna): aquí la página NO se
      // cierra, así que el PUT sí completa.
      flushRef.current?.();
    };
  }, []);

  // Use custom hooks
  useTextInsertion(editor, setPastedText, setDetectedFormat, setConfirmingPaste, setPastePosition, setPastedPlainText);

  // #4: re-emit the floating buttons' position when the editor RESIZES (chat /
  // comments / properties columns opening or closing shrink/grow the editor and
  // recenter the paper). Without this the buttons stay where the paper USED to be
  // — or vanish — until the user re-selects.
  React.useEffect(() => {
    if (!editor) return undefined;
    const container = editorContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    let raf = null;
    const recompute = () => {
      const payload = lastPayloadRef.current;
      if (!payload || !onTextSelection) return;
      const sel = editor.state.selection;
      const { from, to } = sel;
      const stillSelected = from !== to || sel instanceof NodeSelection;
      if (!stillSelected) return;
      let coords;
      try { coords = editor.view.coordsAtPos(from); } catch { return; }
      const containerRect = container.getBoundingClientRect();
      const pageRect = container.querySelector('.editor-paper')?.getBoundingClientRect();
      if (!pageRect) return;
      const top = coords.top - containerRect.top + container.scrollTop;
      const left = Math.min(pageRect.right - containerRect.left + 10, container.clientWidth - 48);
      buttonPositionRef.current = { top, left };
      onTextSelection(payload, { top, left, visible: true }, 'selection');
    };
    const ro = new ResizeObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recompute);
    });
    ro.observe(container);
    return () => { ro.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [editor, onTextSelection]);

  // Expose editor instance to parent via ref
  React.useImperativeHandle(ref, () => ({
    getEditor: () => editor,
    getSelectedNode: () => selectedNode,
    getSelectedNodePos: () => selectedNodePos,
    setSelectedNode: setSelectedNode,
    getScrollContainer: () => editorContainerRef.current,
    // T-028 save-before: force any pending debounced autosave to fire NOW and
    // return its promise so the caller can await persistence before an AI write.
    // No-op (undefined) when nothing is pending.
    flushPendingUpdate: () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = null;
        if (editor && onUpdate) return onUpdate(editor.getJSON());
      }
      return undefined;
    },
    // T-028 refetch-after: apply server content to the editor WITHOUT emitting an
    // update (setContent(..., false)); used after an AI server-side write while
    // the editor is locked. Mirrors the content-sync effect's normalization.
    applyServerContent: (serverContent) => {
      if (!editor) return;
      const normalized = normalizeDocumentContent(migrateSmartTables(serverContent || ''));
      editor.commands.setContent(normalized, false);
    },
    getSelection: () => {
      if (!editor) return '';
      const { from, to } = editor.state.selection;
      return editor.state.doc.textBetween(from, to, ' ').trim();
    },
    hasSelection: () => {
      if (!editor) return false;
      const { from, to } = editor.state.selection;
      return from !== to;
    },
  }));

  // Update content when content prop changes
  useEffect(() => {
    if (!editor || editor.isFocused) return;

    // Reason: Handle both JSON and HTML content for backward compatibility
    const currentContent = JSON.stringify(editor.getJSON());
    let newContent = content || '';

    if (typeof newContent === 'string') {
      // For HTML strings, just compare directly with getHTML
      const currentHTML = editor.getHTML();
      if (currentHTML !== newContent) {
        editor.commands.setContent(newContent, false);
      }
    } else {
      const normalized = normalizeDocumentContent(migrateSmartTables(newContent));
      // For JSON objects, compare stringified versions
      const newContentStr = JSON.stringify(normalized);
      if (currentContent !== newContentStr) {
        editor.commands.setContent(normalized, false);
      }
    }
  }, [content, editor]);

  // Handle Ctrl+Enter for manual page breaks
  useEffect(() => {
    if (!editor) return;

    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        // Insert a page break marker
        editor.chain().focus().insertContent('<div class="page-break"></div>').run();
      }
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('keydown', handleKeyDown);

    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor]);

  // Handle clicks outside editor content to deselect images and clear text selection
  // Reason: Only deselect when clicking on gray area or empty document space, not UI elements
  useEffect(() => {
    if (!editor || !editorContainerRef.current) return;

    const handleContainerClick = (event) => {
      // IMPORTANT: Don't clear selection when clicking on UI elements
      // Check if click is on any interactive UI element
      const isUIElement = event.target.closest('button') ||
                         event.target.closest('input') ||
                         event.target.closest('select') ||
                         event.target.closest('textarea') ||
                         event.target.closest('[role="button"]') ||
                         event.target.closest('[role="combobox"]') ||
                         event.target.closest('[role="listbox"]') ||
                         event.target.closest('[role="option"]') ||
                         event.target.closest('.editor-toolbar') ||
                         event.target.closest('[data-testid="chat-sidebar"]') ||
                         event.target.closest('[data-testid="floating-ai-button"]') ||
                         event.target.closest('[data-testid="quick-command-modal"]');

      if (isUIElement) {
        // Click is on UI element, don't clear selection
        return;
      }

      // Check if click is outside the ProseMirror editor content
      const proseMirrorElement = editorContainerRef.current.querySelector('.ProseMirror');

      if (proseMirrorElement && !proseMirrorElement.contains(event.target)) {
        // Click is outside editor content (in gray area or page margins)
        if (selectedNode) {
          // Clear selection and deselect image
          editor.commands.setTextSelection(0);
          setSelectedNode(null);
          setSelectedNodePos(null);
        }

        // Dispatch event to clear text selection in chat context with flag for gray area click
        window.dispatchEvent(new CustomEvent('textSelected', { detail: { text: '', isGrayAreaClick: true } }));
      } else {
        // Click is inside editor content, check if it's not on an image
        const clickedElement = event.target;
        const isImageClick = clickedElement.tagName === 'IMG' ||
                           clickedElement.closest('.editor-image');

        if (!isImageClick && selectedNode) {
          // Click is on text or empty space within editor, deselect image
          setSelectedNode(null);
          setSelectedNodePos(null);
        }
      }
    };

    const containerElement = editorContainerRef.current;
    containerElement.addEventListener('click', handleContainerClick);

    return () => {
      containerElement.removeEventListener('click', handleContainerClick);
    };
  }, [editor, selectedNode]);

  // Handler functions
  const handleInsertTable = (insertConfig) => {
    const { type, payload } = insertConfig;

    if (type === 'data') {
      const { data, dataType } = payload;

      // Reason: Generate native TipTap table JSON and insert as a real `table` node (RichTable behavior)
      const docJson = generateInitialContent(data, dataType);
      const tableNode = docJson?.content?.find((node) => node.type === 'table');

      if (tableNode) {
        editor.chain().focus().insertContent(tableNode).run();
      } else {
        toast.error('No se pudieron parsear los datos de la tabla. Verifica el formato.');
      }
    } else if (type === 'empty') {
      const { rows, cols } = payload;

      // Reason: Build an empty native table with header row + body rows
      const headerRow = {
        type: 'tableRow',
        content: Array.from({ length: cols }, (_, i) => ({
          type: 'tableHeader',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: `Columna ${i + 1}` }],
            },
          ],
        })),
      };

      const bodyRows = Array.from({ length: Math.max(rows - 1, 1) }, () => ({
        type: 'tableRow',
        content: Array.from({ length: cols }, () => ({
          type: 'tableCell',
          content: [
            {
              type: 'paragraph',
              content: [],
            },
          ],
        })),
      }));

      const tableNode = {
        type: 'table',
        content: [headerRow, ...bodyRows],
      };

      editor.chain().focus().insertContent(tableNode).run();
    }
  };

  const alignTableOrSelection = (alignment, selectionInfo) => {
    const { from, to } = selectionInfo;
    const hasTextSelection = from !== to;

    // Reason: If user has text selected, align the text, not the table
    if (hasTextSelection) {
      const nodeSelection = Boolean(selectionInfo.isNodeSelection);
      if (nodeSelection) {
        // Re-seleccionar el nodo DESPUÉS de alinear (igual que el caso de texto
        // abajo), si no la imagen se deselecciona tras cada alineación.
        editor.chain().focus().setNodeSelection(from).setTextAlign(alignment).setNodeSelection(from).run();
      } else {
        editor.chain().focus().setTextSelection({ from, to }).setTextAlign(alignment).setTextSelection({ from, to }).run();
      }
      return;
    }

    // Sin selección: alinear el CONTENIDO donde está el cursor. Dentro de una
    // celda de tabla, setTextAlign aplica al párrafo de esa celda (tableCell/
    // tableHeader están en los `types` de TextAlign), que es lo que el usuario
    // espera. T-139: antes esta rama alineaba la TABLA ENTERA (attr `align`)
    // cuando el cursor estaba en una celda → el botón "no hacía nada" visible y
    // `isActive` (que mide textAlign de la celda) nunca coincidía. La posición
    // de la tabla en la página se controla aparte, no desde estos botones.
    editor.chain().focus().setTextAlign(alignment).run();
  };

  if (!editor) {
    return <div>Cargando editor...</div>;
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Toolbar — colapsable (T-115) para ganar espacio. Colapsado conserva el
          boton de Chat, porque el toggle del chat vive dentro de EditorToolbar. */}
      {toolbarCollapsed ? (
        <div className="flex items-center gap-2 px-2 py-0.5 border-b border-white/20">
          <button
            type="button"
            onClick={toggleToolbarCollapsed}
            title="Mostrar herramientas"
            className="flex items-center gap-1.5 p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50"
          >
            <ChevronDown className="w-4 h-4" />
            <span className="text-sm">Mostrar herramientas</span>
          </button>
          <button
            type="button"
            onClick={onToggleChat}
            title="Chat IA"
            className={`ml-auto flex items-center p-1 rounded transition-colors ${chatOpen ? 'bg-sky-500 text-white hover:bg-sky-600' : 'hover:bg-sky-100 dark:hover:bg-sky-900/50'}`}
          >
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline text-[10px]">Chat IA</span>
          </button>
        </div>
      ) : (
        <div className="flex items-start">
          <button
            type="button"
            onClick={toggleToolbarCollapsed}
            title="Ocultar herramientas (mas espacio)"
            className="shrink-0 mt-1 ml-1 p-1 rounded hover:bg-white/50 dark:hover:bg-slate-700/50"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <EditorToolbar
              editor={editor}
              savedSelectionRef={savedSelectionRef}
              selectDropdownSelectionRef={selectDropdownSelectionRef}
              onImageUpload={() => {
                cursorPositionRef.current = editor.state.selection.from;
                setImageDialogOpen(true);
              }}
              onImageGenerate={() => {
                cursorPositionRef.current = editor.state.selection.from;
                setImageGenerateDialogOpen(true);
              }}
              onOpenGallery={() => {
                cursorPositionRef.current = editor.state.selection.from;
                setGalleryOpen(true);
              }}
              onInsertTable={() => {
                cursorPositionRef.current = editor.state.selection.from;
                setInsertTableDialogOpen(true);
              }}
              alignTableOrSelection={alignTableOrSelection}
              chatOpen={chatOpen}
              onToggleChat={onToggleChat}
            />
          </div>
        </div>
      )}

      {/* Editor Container */}
      <div
        ref={editorContainerRef}
        className="flex-1 overflow-y-auto editor-canvas p-2 md:p-8 relative"
      >
        <div className="max-w-4xl mx-auto relative">
          <div
            className="editor-paper rounded-lg shadow-lg p-8 md:p-16 continuous-document-with-markers relative"
            style={{
              // T-049: landscape swaps letter-portrait dims (816x1056) -> 1056x816.
              // T-116/T-117: `zoom` llega por prop desde EditorPage (rango 50%-200%,
              // persistente en localStorage). CSS `zoom` nativo para reflow + scroll
              // auto; NO transform:scale (rompe cursor/scroll).
              minHeight: landscape ? '816px' : '1056px',
              width: landscape ? '1056px' : '816px',
              maxWidth: '100%',
              margin: '0 auto',
              zoom,
            }}
          >
            <div
              className="prose max-w-none relative"
              data-testid="tiptap-editor"
            >
              <EditorContent editor={editor} />
              <TableFloatingToolbar editor={editor} />
            </div>
          </div>
        </div>
      </div>

      {/* Image Upload Dialog */}
      <ImageUploadDialog
        open={imageDialogOpen}
        onOpenChange={setImageDialogOpen}
        onInsertImage={(url, alt) => {
          if (editor && cursorPositionRef.current !== null) {
            // Restore cursor position and insert image
            editor.commands.setTextSelection(cursorPositionRef.current);
            editor.chain().focus().setResizableImage({ src: url, alt: alt || '' }).run();
          }
        }}
      />

      {/* Image Generate Dialog */}
      <ImageGenerateDialog
        open={imageGenerateDialogOpen}
        onOpenChange={setImageGenerateDialogOpen}
        onInsertImage={(url, alt) => {
          if (editor && cursorPositionRef.current !== null) {
            // Restore cursor position and insert image
            editor.commands.setTextSelection(cursorPositionRef.current);
            editor.chain().focus().setResizableImage({ src: url, alt: alt || '' }).run();
          }
        }}
      />

      {/* Image Gallery Dialog — direct access from the toolbar */}
      <ImageGallery
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        onSelectImage={(url, alt) => {
          if (editor && cursorPositionRef.current !== null) {
            editor.commands.setTextSelection(cursorPositionRef.current);
            editor.chain().focus().setResizableImage({ src: url, alt: alt || '' }).run();
          }
          setGalleryOpen(false);
        }}
      />

      {/* Confirm Dialog for Table Paste */}
      <ConfirmDialog
        open={isConfirmingPaste}
        onOpenChange={setConfirmingPaste}
        title="¿Deseas convertir a tabla?"
        description="Hemos detectado datos que parecen ser una tabla. ¿Deseas insertarla como una tabla interactiva o pegar el contenido como texto plano?"
        confirmText="Convertir a Tabla"
        cancelText="Pegar Texto"
        onConfirm={() => {
          if (pastedText && detectedFormat && pastePosition !== null) {
            if (detectedFormat === 'html') {
              // Reason (T-014 B7): sanitize Office/Word/Excel/Google-Docs HTML
              // BEFORE insertion — strip MSO styles, <o:p>, spans, widths and
              // rebuild a clean <table> (keeping text + colspan/rowspan) so we
              // never inject foreign markup into the document. Fall back to the
              // raw HTML only if no <table> could be extracted.
              const cleanHtml = sanitizeTableHtml(pastedText) || pastedText;
              editor.chain()
                .focus()
                .setTextSelection(pastePosition)
                .insertContent(cleanHtml, { parseOptions: { preserveWhitespace: false } })
                .run();
              toast.success('Tabla insertada correctamente');
            } else {
              // Reason: For non-HTML formats (md, csv, tsv, json), parse and insert the table
              const docJson = generateInitialContent(pastedText, detectedFormat);
              const tableNode = docJson?.content?.find((node) => node.type === 'table');

              if (tableNode) {
                editor.chain()
                  .focus()
                  .setTextSelection(pastePosition)
                  .insertContent(tableNode)
                  .run();
                toast.success('Tabla insertada correctamente');
              } else {
                toast.error('No se pudo parsear la tabla');
              }
            }
          }
          setConfirmingPaste(false);
          setPastedText(null);
          setPastedPlainText(null);
          setDetectedFormat(null);
          setPastePosition(null);
        }}
        onCancel={() => {
          if (pastePosition !== null) {
            // Reason: Use pastedPlainText (raw text/markdown) instead of pastedText (HTML)
            const textToInsert = pastedPlainText || pastedText || '';
            if (textToInsert) {
              editor.chain()
                .focus()
                .setTextSelection(pastePosition)
                .insertContent(textToInsert)
                .run();
            }
          }
          setConfirmingPaste(false);
          setPastedText(null);
          setPastedPlainText(null);
          setDetectedFormat(null);
          setPastePosition(null);
        }}
      />

      {/* Insert Table Dialog */}
      <InsertTableDialog
        open={isInsertTableDialogOpen}
        onOpenChange={setInsertTableDialogOpen}
        onInsert={handleInsertTable}
      />
    </div>
  );
});

ContinuousDocument.displayName = 'ContinuousDocument';

export default ContinuousDocument;
